/* eslint-disable @typescript-eslint/prefer-optional-chain */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  useTheme,
  TextField,
  Button,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import {
  getCustomerById,
  updateCustomerService,
  updateAvcDetails,
  updateFullPartialDeclaration,
} from 'service/CustomerService';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import { colorTheme } from 'theme';
import Avatar from '@mui/material/Avatar';
import { type ICustomerResponse, type IReuseGridProps } from 'types';
import { dateFormat, truncateEmail } from 'utils/GeneralUtil';
import { DialogBox } from 'components';
import MuiPhoneNumber from 'material-ui-phone-number';
import type { RootState } from 'store/rootReducer';
import { useDispatch, useSelector } from 'react-redux';
import { setSnackBarSuccess } from 'store/snackBar';
import { strings } from 'i18n/Strings';
import EditCustomerDetailsModel from './EditCustomerDetailsModel';
import { getResource } from 'service/UserService';
import Buttons from 'components/elements/Buttons';
import EditWatchListModel from './EditWatchListModel';

interface Category {
  id: number;
  categoryName: string;
  parentId: number | null;
  parent_id: number | null;
  subCategories?: Category[];
}

const PersonalDetails = (): JSX.Element => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const colors = colorTheme(theme.palette.mode);
  const [customerInfo, setCustomerInfo] = useState<ICustomerResponse>();
  const [image, setImage] = useState('');
  const { id } = useParams();
  const [openDialog, setOpenDialog] = useState(false);
  const [editedEmail, setEditedEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [openPhoneDialog, setOpenPhoneDialog] = useState(false);
  const [openUpdateDetails, setOpenUpdateDetails] = useState(false);
  const [editedPhone, setEditedPhone] = useState('');
  const [loader, setLoader] = useState(false);
  const [openAvcEdit, setOpenAvcEdit] = useState(false);
  const [openWatchlist, setOpenWatchlist] = useState(false);

  const [openFullPartialEdit, setOpenFullPartialEdit] = useState(false);
  const { economicStatusAndOccupation } = useSelector(
    (state: RootState) => state.employeeDetails,
  );
  const fullPartialDeclaration = (
    customerInfo as ICustomerResponse & { fullPartialDeclaration?: string }
  )?.fullPartialDeclaration;
  const isFullPartialYes =
    String(fullPartialDeclaration ?? '').toLowerCase() === 'yes';
  const getCustomerData = async (): Promise<void> => {
    try {
      const response = (await getCustomerById(Number(id))) as ICustomerResponse;
      setCustomerInfo(
        response?.basicInfo as unknown as ICustomerResponse | undefined,
      );
      if (response?.basicInfo?.profileImage) {
        const profilePicSignedUrl = await getResource(
          response.basicInfo.profileImage,
        );
        if (profilePicSignedUrl.url) setImage(profilePicSignedUrl.url);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (id) {
      void getCustomerData();
    }
  }, []);

  const handleCloseDialog = (): void => {
    setOpenDialog(false);
  };

  const handleClosePhoneDialog = (): void => {
    setOpenPhoneDialog(false);
  };

  const handlePhoneChange = (value: string): void => {
    setEditedPhone(value);
  };
  const findObjectById = (arr: Category[], id: number): Category | null => {
    if (arr) {
      for (const obj of arr) {
        if (obj.id === id) {
          return obj;
        }
        if (obj.subCategories && obj.subCategories.length > 0) {
          const result = findObjectById(obj.subCategories, id);
          if (result) {
            return result;
          }
        }
      }
      return null;
    }
    return null;
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleSaveCustomerData = async () => {
    if (validateEmail(editedEmail) || editedEmail === '') {
      setLoader(true);
      await updateCustomerService(id, editedEmail, editedPhone)
        .then((response) => {
          void getCustomerData();
          setOpenDialog(false);
          setOpenPhoneDialog(false);
          dispatch(
            setSnackBarSuccess({
              snackBarMessage: strings.displayText.detailsUpdatedSuccussfully,
            }),
          );
        })
        .catch((error) => {
          console.error('Error saving data:', error);
        });
      setLoader(false);
    } else {
      setEmailError('Please enter a valid email address');
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const ReuseGrid = (props: IReuseGridProps): JSX.Element => (
    <Grid item xs={12} sm={12} md={12} sx={{ display: 'flex', padding: 1 }}>
      <Grid item xs={4} sm={4} md={4} paddingLeft={1}>
        <Typography
          component="h5"
          variant="h5"
          color="#838383"
          fontWeight={700}
          fontSize={16}
          fontFamily="Inter !important"
        >
          {props.customName}
        </Typography>
      </Grid>
      <Grid item xs={3} sm={3} md={3}>
        <Typography>:</Typography>
      </Grid>
      <Grid item xs={4} sm={4} md={4}>
        <Typography
          component="h5"
          variant="h5"
          color={colors.gray[100]}
          fontWeight={700}
          fontSize={16}
          fontFamily="Inter !important"
        >
          {props.customerDetails}
        </Typography>
      </Grid>
      {props.isEditable ? (
        <Grid item xs={1} sm={1} md={1}>
          <ModeEditIcon
            style={{ cursor: 'pointer' }}
            onClick={() => {
              props.onClick && props.onClick();
            }}
          />
        </Grid>
      ) : (
        ''
      )}
    </Grid>
  );
  return (
    <Box>
      <Paper
        sx={{
          margin: 'auto',

          flexGrow: 1,
        }}
      >
        <Grid
          container
          item
          xs={12}
          sm={12}
          md={12}
          sx={{
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              right: 40,
              top: 20,
            }}
          >
            <Button
              variant="contained"
              sx={{
                height: 40,
                bgcolor: colors.greenAccent[500],
                borderRadius: 2,
                textTransform: 'none',
              }}
              onClick={() => {
                setOpenUpdateDetails(true);
              }}
            >
              Edit Details
            </Button>
          </Box>
          <Grid item xs={3} sm={3} md={3} sx={{ marginTop: '30px' }}>
            <Box
              sx={{
                padding: 2,
              }}
            >
              <Typography
                component="h5"
                variant="h5"
                color={colors.greenAccent[500]}
                fontWeight={800}
                fontSize={18}
                fontFamily="Inter !important"
                sx={{
                  textAlign: 'center',
                  marginBlock: 2,
                }}
              >
                {strings.displayText.profilePicture}
              </Typography>
              <Box marginBottom={5}></Box>
              <Box
                sx={{
                  alignSelf: 'center',
                  justifyContent: 'center',
                  alignItems: 'center',
                  display: 'flex',
                }}
              >
                {customerInfo?.profileImage ? (
                  <Avatar
                    sx={{
                      height: '100px',
                      width: '100px',
                      alignSelf: 'center',
                      bgcolor: '#e4dfed',
                      color: '#202020',
                      fontWeight: 700,
                      fontSize: 40,
                    }}
                    alt="img"
                    src={image}
                  />
                ) : (
                  // <img src={image} />
                  <Avatar
                    sx={{
                      width: '100px',
                      height: '100px',
                      alignSelf: 'center',
                      bgcolor: '#B5DFC8',
                      color: '#202020',
                      fontFamily: 'Inter',
                      fontWeight: 700,
                      fontSize: 40,
                    }}
                  >
                    {customerInfo?.firstName &&
                      customerInfo?.firstName.slice(0, 1) +
                        customerInfo?.lastName.slice(0, 1)}
                  </Avatar>
                )}
              </Box>
            </Box>
          </Grid>

          <Grid
            item
            xs={8}
            sm={8}
            md={8}
            sx={{ marginBlock: 2, marginTop: '50px' }}
          >
            <Box marginTop={1}></Box>
            <ReuseGrid
              customName={strings.displayText.fullName}
              customerDetails={
                customerInfo?.firstName
                  ? `${customerInfo?.firstName} ${customerInfo?.lastName}`
                  : '-'
              }
            />
            <ReuseGrid
              customName={strings.displayText.emailAddress}
              customerDetails={truncateEmail(
                customerInfo?.email ? customerInfo?.email : '-',
              )}
            />
            <ReuseGrid
              customName={strings.displayText.phoneNumber}
              customerDetails={
                customerInfo?.phone
                  ? `${customerInfo?.callingCode?.startsWith('+') ? '' : '+'}${
                      customerInfo?.callingCode
                    } ${customerInfo?.phone}`
                  : '-'
              }
            />
            <Divider sx={{ marginBlock: 2 }} />
            <ReuseGrid
              customName={strings.displayText.countryOfBirth}
              customerDetails={
                customerInfo?.country ? customerInfo?.country : '-'
              }
            />
            <ReuseGrid
              customName={strings.displayText.citizenship}
              customerDetails={
                customerInfo?.citizenship ? customerInfo?.citizenship : '-'
              }
            />
            <ReuseGrid
              customName={strings.displayText.dateOfBirth}
              customerDetails={
                customerInfo?.dob ? dateFormat(customerInfo?.dob) : '-'
              }
            />
            <ReuseGrid
              customName={strings.displayText.gender}
              customerDetails={
                customerInfo?.gender
                  ? (() => {
                      const genderStr = String(customerInfo.gender);
                      return `${genderStr.charAt(0).toUpperCase()}${genderStr
                        .slice(1)
                        .toLowerCase()}`;
                    })()
                  : '-'
              }
            />
            <ReuseGrid
              customName={strings.displayText.maritalStatus}
              customerDetails={
                customerInfo?.maritalStatus
                  ? (() => {
                      const status = String(
                        customerInfo.maritalStatus,
                      ).toLowerCase();
                      if (status === 'married') return 'Married';
                      if (status === 'single') return 'Single';
                      if (status === 'civil partnership')
                        return 'Civil Partnership';
                      return `${status.charAt(0).toUpperCase()}${status.slice(
                        1,
                      )}`;
                    })()
                  : '-'
              }
            />
            <ReuseGrid
              customName={strings.displayText.address}
              customerDetails={
                customerInfo?.address ? customerInfo?.address : '-'
              }
            />

            <Divider sx={{ marginBlock: 2 }} />
            <ReuseGrid
              customName={'Watchlist - Sanctions hit'}
              customerDetails={
                customerInfo?.watchlistSanctionsHit === true
                  ? 'Yes'
                  : customerInfo?.watchlistSanctionsHit === false
                  ? 'No'
                  : '-'
              }
              isEditable={
                customerInfo?.watchlistSanctionsHit === true ||
                customerInfo?.watchlistPepHit === true
              }
              onClick={() => {
                setOpenWatchlist(true);
              }}
            />
            <ReuseGrid
              customName={'Watchlist - PEP hit'}
              customerDetails={
                customerInfo?.watchlistPepHit === true
                  ? 'Yes'
                  : customerInfo?.watchlistPepHit === false
                  ? 'No'
                  : '-'
              }
            />
            <ReuseGrid
              customName={'Watchlist last checked'}
              customerDetails={
                customerInfo?.watchlistLastCheckedAt
                  ? dateFormat(customerInfo.watchlistLastCheckedAt)
                  : '-'
              }
            />
            <Divider sx={{ marginBlock: 2 }} />
            <ReuseGrid
              customName={strings.displayText.employeeContribution}
              customerDetails={
                customerInfo?.employeeContribution != null &&
                customerInfo?.employeeContribution !== ''
                  ? customerInfo.employeeContribution
                  : '-'
              }
            />
            <ReuseGrid
              customName={strings.displayText.employerContribution}
              customerDetails={
                customerInfo?.employerContribution != null &&
                customerInfo?.employerContribution !== ''
                  ? customerInfo.employerContribution
                  : '-'
              }
            />
            <ReuseGrid
              customName={strings.displayText.employeeContributionPercentage}
              customerDetails={
                customerInfo?.employeeContributionPercentage != null &&
                customerInfo?.employeeContributionPercentage !== ''
                  ? customerInfo.employeeContributionPercentage
                  : '-'
              }
            />
            <ReuseGrid
              customName={strings.displayText.employerContributionPercentage}
              customerDetails={
                customerInfo?.employerContributionPercentage != null &&
                customerInfo?.employerContributionPercentage !== ''
                  ? customerInfo.employerContributionPercentage
                  : '-'
              }
            />

            <Divider sx={{ marginBlock: 2 }} />
            <ReuseGrid
              customName={strings.displayText.economicStatus}
              customerDetails={
                customerInfo?.economicStatus
                  ? economicStatusAndOccupation?.categories?.find(
                      (val) =>
                        val.id ===
                        findObjectById(
                          economicStatusAndOccupation?.categories,
                          customerInfo.economicStatus,
                        )?.parentId,
                    )?.categoryName ?? '-'
                  : '-'
              }
            />
            <ReuseGrid
              customName={strings.displayText.economicOccupation}
              customerDetails={
                customerInfo?.economicStatus
                  ? findObjectById(
                      economicStatusAndOccupation?.categories,
                      customerInfo.economicStatus,
                    )?.categoryName ?? '-'
                  : '-'
              }
            />
            <ReuseGrid
              customName={strings.displayText.ppsn}
              customerDetails={
                customerInfo?.ppsnNumber ? customerInfo?.ppsnNumber : '-'
              }
            />
            <ReuseGrid
              customName={strings.displayText.pepText}
              customerDetails={customerInfo?.pepType ? 'Yes' : 'No'}
            />

            <Divider sx={{ marginBlock: 2 }} />
            <ReuseGrid
              customName="AVC Details"
              customerDetails={
                customerInfo?.isAvcPensionUser === null
                  ? '-'
                  : customerInfo?.isAvcPensionUser
                  ? 'Yes'
                  : 'No'
              }
              isEditable
              onClick={() => {
                setOpenAvcEdit(true);
              }}
            />
            <ReuseGrid
              customName={strings.displayText.fullPartialDeclaration}
              customerDetails={
                fullPartialDeclaration ? (isFullPartialYes ? 'Yes' : 'No') : '-'
              }
              isEditable
              onClick={() => {
                setOpenFullPartialEdit(true);
              }}
            />
            <ReuseGrid
              customName="Approval Status"
              customerDetails={
                customerInfo?.approvalStatus ? customerInfo.approvalStatus : '-'
              }
            />
          </Grid>
        </Grid>
      </Paper>
      <DialogBox
        open={openDialog}
        onClose={handleCloseDialog}
        className={''}
        fullWidth={false}
        title="Edit Email"
      >
        <DialogContent>
          <TextField
            label="Edit Email"
            value={editedEmail}
            onChange={(e) => {
              setEditedEmail(e.target.value);
              if (emailError) {
                setEmailError('');
              }
            }}
            error={!!emailError}
            helperText={emailError}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            endIcon={loader ? <CircularProgress size={15} /> : null}
            onClick={handleSaveCustomerData}
          >
            Save
          </Button>
        </DialogActions>
      </DialogBox>

      <DialogBox
        open={openPhoneDialog}
        onClose={handleClosePhoneDialog}
        className={''}
        fullWidth={false}
        title="Edit Phone Number"
      >
        <DialogContent>
          <MuiPhoneNumber
            required={true}
            label="Edit Phone Number"
            name="editedPhone"
            defaultCountry={'ie'}
            variant="outlined"
            fullWidth
            onChange={(value: string) => {
              handlePhoneChange(value);
            }}
            value={editedPhone}
            sx={{ '& p': { color: 'red' }, my: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePhoneDialog}>Cancel</Button>
          <Button
            onClick={handleSaveCustomerData}
            endIcon={loader ? <CircularProgress size={15} /> : null}
          >
            Save
          </Button>
        </DialogActions>
      </DialogBox>
      <EditWatchListModel
        open={openWatchlist}
        setOpenWatchlist={setOpenWatchlist}
        watchlistSanctionsHit={customerInfo?.watchlistSanctionsHit}
        watchlistPepHit={customerInfo?.watchlistPepHit}
        cutomerId={Number(id)}
        handleSuccess={() => {
          setOpenWatchlist(false);
          void getCustomerData();
        }}
      />

      <DialogBox
        open={openUpdateDetails}
        onClose={() => {
          setOpenUpdateDetails(false);
        }}
        className={''}
        fullWidth={true}
        title="Edit Customer Details"
      >
        <EditCustomerDetailsModel
          customerInfo={customerInfo}
          getCustomerData={getCustomerData}
          setOpenUpdateDetails={setOpenUpdateDetails}
        />
      </DialogBox>

      <DialogBox
        open={openAvcEdit}
        onClose={() => {
          setOpenAvcEdit(false);
        }}
        title={strings.displayText.confirmAvcChange}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography
              component="h5"
              variant="h5"
              fontWeight={700}
              fontSize={16}
              fontFamily="Inter !important"
            >
              {customerInfo?.isAvcPensionUser
                ? strings.displayText.avcYesToNo1
                : strings.displayText.avcNoToYes1}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography
              component="h6"
              variant="h6"
              fontSize={16}
              fontFamily="Inter !important"
            >
              {customerInfo?.isAvcPensionUser
                ? strings.displayText.avcYesToNo2
                : strings.displayText.avcNoToYes2}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Grid
              container
              direction="row"
              spacing={2}
              sx={{
                justifyContent: 'flex-end',
                alignItems: 'center',
              }}
            >
              <Buttons
                onClick={() => {
                  setOpenAvcEdit(false);
                }}
                variant="contained"
                sx={{
                  m: 1,
                  bgcolor: colors.gray[900],
                  borderRadius: 2,
                }}
              >
                <Typography
                  component="h1"
                  variant="h5"
                  style={{ fontSize: '12px', color: colors.primaryBg[700] }}
                >
                  Cancel
                </Typography>
              </Buttons>
              <Buttons
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onClick={async () => {
                  await updateAvcDetails(
                    Number(id),
                    !customerInfo.isAvcPensionUser,
                  )
                    .then((_response) => {
                      void getCustomerData();
                      setOpenAvcEdit(false);
                      dispatch(
                        setSnackBarSuccess({
                          snackBarMessage:
                            strings.displayText.detailsUpdatedSuccussfully,
                        }),
                      );
                    })
                    .catch((error) => {
                      console.error('Error saving data:', error);
                    });
                }}
                variant="contained"
                sx={{
                  m: 1,
                  bgcolor: colors.greenAccent[500],
                  borderRadius: 2,
                }}
              >
                <Typography
                  component="h1"
                  variant="h5"
                  style={{ fontSize: '12px' }}
                >
                  Update
                </Typography>
              </Buttons>
            </Grid>
          </Grid>
        </Grid>
      </DialogBox>

      <DialogBox
        open={openFullPartialEdit}
        onClose={() => {
          setOpenFullPartialEdit(false);
        }}
        className={''}
        fullWidth={false}
        title={`Confirm ${strings.displayText.fullPartialDeclaration} Change`}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography
              component="h5"
              variant="h5"
              fontWeight={700}
              fontSize={16}
              fontFamily="Inter !important"
            >
              {isFullPartialYes
                ? `Do you want to change ${strings.displayText.fullPartialDeclaration} from Yes to No?`
                : `Do you want to change ${strings.displayText.fullPartialDeclaration} from No to Yes?`}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography
              component="h6"
              variant="h6"
              fontSize={16}
              fontFamily="Inter !important"
            >
              Click Update to confirm this change.
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Grid
              container
              direction="row"
              spacing={2}
              sx={{
                justifyContent: 'flex-end',
                alignItems: 'center',
              }}
            >
              <Buttons
                onClick={() => {
                  setOpenFullPartialEdit(false);
                }}
                variant="contained"
                sx={{
                  m: 1,
                  bgcolor: colors.gray[900],
                  borderRadius: 2,
                }}
              >
                <Typography
                  component="h1"
                  variant="h5"
                  style={{ fontSize: '12px', color: colors.primaryBg[700] }}
                >
                  Cancel
                </Typography>
              </Buttons>
              <Buttons
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onClick={async () => {
                  await updateFullPartialDeclaration(
                    Number(id),
                    isFullPartialYes ? 'No' : 'Yes',
                  )
                    .then((_response) => {
                      void getCustomerData();
                      setOpenFullPartialEdit(false);
                      dispatch(
                        setSnackBarSuccess({
                          snackBarMessage:
                            strings.displayText.detailsUpdatedSuccussfully,
                        }),
                      );
                    })
                    .catch((error) => {
                      console.error('Error saving data:', error);
                    });
                }}
                variant="contained"
                sx={{
                  m: 1,
                  bgcolor: colors.greenAccent[500],
                  borderRadius: 2,
                }}
              >
                <Typography
                  component="h1"
                  variant="h5"
                  style={{ fontSize: '12px' }}
                >
                  Update
                </Typography>
              </Buttons>
            </Grid>
          </Grid>
        </Grid>
      </DialogBox>
    </Box>
  );
};

export default PersonalDetails;
